import {ApplicationData, Question} from '@agenium-ai/agenium-client-sdk/dist/models';
import {Section} from '@agenium-ai/agenium-client-sdk/dist/models/section';
import DateFnsUtils from '@date-io/date-fns';
import {
    Box,
    Button,
    Checkbox,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    MenuItem,
    Select,
    Typography,
    makeStyles,
} from '@material-ui/core';
import KeyboardBackspaceIcon from '@material-ui/icons/KeyboardBackspace';
import {KeyboardDatePicker, MuiPickersUtilsProvider} from '@material-ui/pickers';
import {MaterialUiPickersDate} from '@material-ui/pickers/typings/date';
import 'date-fns';
import {format} from 'date-fns';
import {MaskedPattern} from 'imask';
import {observer} from 'mobx-react-lite';
import React, {FC, PropsWithChildren, useCallback, useEffect, useState} from 'react';
import {firstValueFrom} from 'rxjs';
import {useAgeniumUser} from '../../../Core/hooks/useAgeniumUser';
import {ParameterStore} from '../../../Core/mobx/ParameterStore/ParameterStore';
import RootStore from '../../../Core/mobx/RootStore';
import InputText from '../../../UI/EmcApplicationQuestions/inputs/core/InputText';
import {Outlist} from '../InstantAppModal';
import {getApprovalQuoteFromCalcfocus} from './utils';
import {AgenuimContextType} from '@/Core/agenium/ageniumContext';
import {AgeniumAgentDetail} from '@/Core/agenium/types';
import {getAgentDetail} from '@/Core/agenium/agentDetails';
import API from '../../../APIs/API';
import {BankVerificationState, BankVerificationResponse} from '../../../APIs/types';

const NEW_MAX_EFFECTIVE_ENFORCE_DT = new Date(Date.UTC(2025, 5, 18)); // 18 Jun 2025 in UTC

interface BankingInformationProps {
    parameterStore: ParameterStore;
    onExit: () => void;
    onNext: () => void;
    calcFocusDataLocal: {
        faceAmount?: number;
        monthlyPremium?: number;
        productName?: string;
        req?: any;
        out?: Outlist[];
    };
    ageniumContext: AgenuimContextType;
    onBack?: () => void;
}
const useStyles = makeStyles(() => ({
    wrapper: {
        padding: '20px',
    },
    title: {
        fontFamily: 'LoraRegular',
        fontStyle: 'normal',
        fontWeight: 600,
        fontSize: '44px',
        lineHeight: '48.4px',
        color: '#3f51b5',
        marginBottom: '28px',
    },
    formControl: {
        minWidth: 240,
    },
    formSection: {
        width: '100%',
        marginBottom: 24,
    },
    formSectionTitle: {
        fontFamily: 'LoraRegular',
        fontWeight: 700,
        fontSize: '24px',
        lineHeight: '30.72px',
        letterSpacing: '2%',
        color: '#434343',
        padding: '30px 0 20px',
    },
    label: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '30.27px',
        color: '#838383',
    },
    p1: {
        fontFamily: 'FredokaLight',
        fontWeight: 400,
        fontSize: '18px',
        lineHeight: '28.8px',
        color: '#434343',
    },
    disclamerWrapper: {
        border: '1px solid #3f51b5',
        borderRadius: '6px',
        padding: '18px 16px',
        color: '#3f51b5',
    },
    disclamerTitle: {
        fontFamily: 'FredokaRegular',
        fontWeight: 600,
        fontSize: '15px',
        lineHeight: '25.6px',
        marginBottom: '10px',
    },
    disclamerText: {
        fontFamily: 'FredokaRegular',
        fontWeight: 400,
        fontSize: '15px',
        lineHeight: '24px',
    },
    navigation: {
        marginTop: '20px',
        flexWrap: 'nowrap',
    },
    backBtn: {
        borderRadius: '20px',
        minWidth: '145px',
    },
    nextBtn: {
        width: '100%',
        borderRadius: '20px',
    },
    btnWrapper: {
        width: '100%',
        minWidth: '200px',
        maxWidth: '400px',
    },
    datePicker: {
        '& .MuiTextField-root': {
            marginTop: 0,
            marginBottom: 0,
        },
    },
    errorMessage: {
        backgroundColor: '#ffebee',
        border: '1px solid #f44336',
        borderRadius: '4px',
        padding: '12px 16px',
        marginBottom: '16px',
        color: '#d32f2f',
        fontFamily: 'FredokaRegular',
        fontSize: '14px',
        lineHeight: '20px',
    },
    overrideCheckbox: {
        marginTop: '8px',
        '& .MuiFormControlLabel-label': {
            fontFamily: 'FredokaRegular',
            fontSize: '14px',
            color: '#434343',
        },
    },
}));

const accountTypeToAgeniumMap = {
    CHECKING: 'Checking',
    SAVINGS: 'Savings',
};

// Reverse mapping to convert from Agenium format to parameter store format
const ageniumToAccountTypeMap = {
    Checking: 'CHECKING',
    Savings: 'SAVINGS',
};

const BankingInformation: React.FC<BankingInformationProps> = observer(
    ({onNext, onBack, onExit, parameterStore, calcFocusDataLocal, ageniumContext}) => {
        const {ageniumUser, ageniumUserPromise} = useAgeniumUser();
        const classes = useStyles();
        const [case_Bank_RoutingNumber, setCase_Bank_RoutingNumber] = useState<Question>();
        const [case_Bank_AccountNumber, setCase_Bank_AccountNumber] = useState<Question>();
        const [case_Bank_AccountType, setCase_Bank_AccountType] = useState<Question>();
        const [case_Applicant_LapseNotice, setCase_Applicant_LapseNotice] = useState<Question>();
        const [case_Applicant_PaymentMode, setCase_Applicant_PaymentMode] = useState<Question>();
        const [case_Applicant_OtherPaymentMode, setCase_Applicant_OtherPaymentMode] = useState<Question>();
        const [case_Applicant_PaymentModeEFTInitialDraft, setCase_Applicant_PaymentModeEFTInitialDraft] =
            useState<Question>();
        const [case_Applicant_PaymentFrequency, setCase_Applicant_PaymentFrequency] = useState<Question>();
        const [case_Applicant_PaymentFrequencyOther, setCase_Applicant_PaymentFrequencyOther] = useState<Question>();
        const [case_Applicant_RequestedEffectiveDate, setCase_Applicant_RequestedEffectiveDate] = useState<Question>();
        const [case_Applicant_FaceAmountFinal, setCase_Applicant_FaceAmountFinal] = useState<Question>();
        const [case_AgentReport_AgentAssisted, setCase_AgentReport_AgentAssisted] = useState<Question>();
        const [case_AgentReport_AgentAssistedContractNumber, setCase_AgentReport_AgentAssistedContractNumber] =
            useState<Question>();
        const [selectedEffectiveDate, setSelectedEffectiveDate] = useState<MaterialUiPickersDate>(null);
        const [isFirstNextCall, setIsFirstNextCall] = useState(true);
        const [initLoadAppData, setInitLoadAppData] = useState(false);
        const [agentDetail, setAgentDetail] = useState<AgeniumAgentDetail | null>();

        const searchParams = new URLSearchParams(location.search);
        const effectiveDateOverride =
            (searchParams.get('effectiveDateOverride')?.toLowerCase() as string | 'false') === 'true';

        const isEstLateHour = () =>
            new Date(new Date().toLocaleString('en-US', {timeZone: 'America/New_York'})).getHours() > 20;

        const effectiveDatesDisable = (date: MaterialUiPickersDate) => {
            const d = date?.getDate();
            return d === 29 || d === 30 || d === 31;
        };

        const [submitting, setSubmitting] = useState(false);
        const [bankVerificationResult, setBankVerificationResult] = useState<BankVerificationResponse | null>(null);
        const [verificationInProgress, setVerificationInProgress] = useState(false);
        const [overrideConfirmed, setOverrideConfirmed] = useState(false);
        const FormSection: FC<PropsWithChildren<any>> = useCallback(
            ({title, children}: {title: string; children: any}) => (
                <Box className={classes.formSection}>
                    <Box className={classes.formSectionTitle}>{title}</Box>
                    {children}
                </Box>
            ),
            [],
        );
        const {onValueChangedBanking, banking} = parameterStore;

        const onError = (error: any) => {
            RootStore.uiStore.showSnackbar(error?.message, 'error');
            setSubmitting(false);
        };

        useEffect(() => {
            if (ageniumUserPromise) {
                ageniumUserPromise.then((au) => {
                    if (au === null) {
                        setAgentDetail(null);
                        return;
                    }
                    const detail = getAgentDetail(au);
                    setAgentDetail(detail);
                });
            }
        }, [ageniumUserPromise, ageniumUser]);
        useEffect(() => {
            if (ageniumContext.applicationNumber) {
                ageniumContext?.sdk?.getSections().subscribe({
                    next: (s: Section[] | undefined) => {
                        let continueSearch = 25;
                        const lookForKey = (questions: any) => {
                            for (let i = 0; continueSearch && questions && i < questions.length; i++) {
                                if (questions[i]?.key === 'Case_Bank_RoutingNumber') {
                                    setCase_Bank_RoutingNumber(questions[i]);
                                    if (questions[i].value)
                                        onValueChangedBanking(banking.routingNumber, questions[i].value);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_Bank_AccountNumber') {
                                    setCase_Bank_AccountNumber(questions[i]);
                                    if (questions[i].value)
                                        onValueChangedBanking(banking.accountNumber, questions[i].value);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_Bank_AccountType') {
                                    setCase_Bank_AccountType(questions[i]);
                                    const upperValue =
                                        ageniumToAccountTypeMap[questions[i].value as 'Checking' | 'Savings'] ||
                                        'CHECKING';
                                    onValueChangedBanking(banking.accountType, upperValue);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_Applicant_LapseNotice') {
                                    setCase_Applicant_LapseNotice(questions[i]);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_Applicant_PaymentMode') {
                                    setCase_Applicant_PaymentMode(questions[i]);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_Applicant_OtherPaymentMode') {
                                    setCase_Applicant_OtherPaymentMode(questions[i]);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_Applicant_PaymentModeEFTInitialDraft') {
                                    setCase_Applicant_PaymentModeEFTInitialDraft(questions[i]);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_Applicant_PaymentFrequency') {
                                    setCase_Applicant_PaymentFrequency(questions[i]);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_Applicant_PaymentFrequencyOther') {
                                    setCase_Applicant_PaymentFrequencyOther(questions[i]);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_Applicant_RequestedEffectiveDate') {
                                    setCase_Applicant_RequestedEffectiveDate(questions[i]);
                                    const date = new Date();
                                    if (isEstLateHour()) date.setDate(date.getDate() + 1);
                                    if (questions[i].value && new Date(questions[i].value) > date)
                                        setSelectedEffectiveDate(new Date(questions[i].value));
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_Applicant_FaceAmountFinal') {
                                    setCase_Applicant_FaceAmountFinal(questions[i]);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_AgentReport_AgentAssisted') {
                                    setCase_AgentReport_AgentAssisted(questions[i]);
                                    continueSearch--;
                                } else if (questions[i]?.key === 'Case_AgentReport_AgentAssistedContractNumber') {
                                    setCase_AgentReport_AgentAssistedContractNumber(questions[i]);
                                    continueSearch--;
                                }
                                lookForKey(questions[i].questions);
                            }
                        };
                        for (let t = 0; s && t < s.length; t++) lookForKey(s[t].questions);
                    },
                });
            }
        }, [ageniumContext.applicationNumber]);

        useEffect(() => {
            if (!initLoadAppData && ageniumContext.applicationNumber && !ageniumContext.applicationData) {
                setInitLoadAppData(true);
                ageniumContext.loadApplicationData();
            }
        }, [initLoadAppData, ageniumContext.applicationNumber, ageniumContext.applicationData]);

        useEffect(() => {
            ageniumContext.initSdk();
        }, []);

        useEffect(() => {
            setBankVerificationResult(null);
            setOverrideConfirmed(false);
        }, [banking.routingNumber.value, banking.accountNumber.value, banking.accountType.value]);

        const handleOverride = async (verificationId: string, accountHash: string) => {
            try {
                await API.OverrideBankVerification({
                    verificationId,
                    accountHash,
                    reason: 'Agent confirmed bank information is correct after speaking with client',
                });
            } catch {
                // Fail silently
            }
        };

        const verifyBankAccount = async () => {
            if (!banking.routingNumber.value || !banking.accountNumber.value || !banking.accountType.value) {
                return null;
            }

            setVerificationInProgress(true);
            setBankVerificationResult(null);
            setOverrideConfirmed(false);

            try {
                const accountTypeNumeric = banking.accountType.value === 'CHECKING' ? 1 : 2;

                // Get customer ID and other audit fields from agenium context
                // applicationData is an array of {key, value} objects
                const findValue = (key: string) =>
                    ageniumContext.applicationData?.find((t: any) => t.key === key)?.value;

                const customerEmail = parameterStore?.contact;
                const ageniumCaseId = findValue('Case_CaseID') || findValue('CaseID') || findValue('case_CaseID');
                const formId = parameterStore?.formid;

                // Get customer name from parameterStore
                const firstName =
                    parameterStore?.approval?.firstName?.value ||
                    (parameterStore as any)?._walkthroughRawData?.first_name;
                const lastName =
                    parameterStore?.approval?.lastName?.value ||
                    (parameterStore as any)?._walkthroughRawData?.last_name;

                const result = await API.VerifyBankAccount({
                    routingNumber: banking.routingNumber.value,
                    accountNumber: banking.accountNumber.value,
                    accountType: accountTypeNumeric,
                    customerEmail: customerEmail, // Required for consent check
                    customerFirstName: firstName || undefined,
                    customerLastName: lastName || undefined,
                    formId: formId,
                    ageniumCaseId: ageniumCaseId,
                });

                if (result) {
                    setBankVerificationResult(result);
                }
                return result;
            } catch (error) {
                console.error('Bank verification failed:', error);
                RootStore.uiStore.showSnackbar('Bank verification failed. Please try again.', 'error');
                return null;
            } finally {
                setVerificationInProgress(false);
            }
        };

        const canProceedWithVerification = (result?: BankVerificationResponse | null) => {
            const verificationToCheck = result !== undefined ? result : bankVerificationResult;
            if (!verificationToCheck) return false;
            if (verificationToCheck.state === BankVerificationState.PASS) return true;
            if (verificationToCheck.state === BankVerificationState.PREVENT_NEXT_WITH_OVERRIDE && overrideConfirmed)
                return true;
            return false;
        };

        const submitValues = async () => {
            if (
                case_Bank_RoutingNumber &&
                case_Bank_AccountNumber &&
                case_Bank_AccountType &&
                case_Applicant_LapseNotice &&
                case_Applicant_PaymentMode &&
                case_Applicant_OtherPaymentMode &&
                case_Applicant_PaymentModeEFTInitialDraft &&
                case_Applicant_PaymentFrequency &&
                case_Applicant_PaymentFrequencyOther &&
                case_Applicant_RequestedEffectiveDate &&
                case_AgentReport_AgentAssisted &&
                case_AgentReport_AgentAssistedContractNumber &&
                banking.routingNumber.value &&
                banking.accountNumber.value &&
                banking.accountType.value &&
                selectedEffectiveDate
            ) {
                // Verify if not already done
                let verificationToUse = bankVerificationResult;
                if (!bankVerificationResult) {
                    const newResult = await verifyBankAccount();
                    verificationToUse = newResult;
                }

                // Check if we can proceed after verification
                if (!canProceedWithVerification(verificationToUse)) {
                    RootStore.uiStore.showSnackbar(
                        'Please resolve bank verification issues before proceeding.',
                        'error',
                    );
                    return;
                }

                // If proceeding with override, call the override API
                if (
                    verificationToUse &&
                    verificationToUse.state === BankVerificationState.PREVENT_NEXT_WITH_OVERRIDE &&
                    overrideConfirmed
                ) {
                    await handleOverride(verificationToUse.verificationId, verificationToUse.accountHash || '');
                }

                RootStore.agentPortalStore.contactsFormStore.saveForm();
                setSubmitting(true);
                //026009593 -- testing routing number
                case_Bank_RoutingNumber.value = banking.routingNumber.value;
                case_Bank_AccountNumber.value = banking.accountNumber.value;
                case_Bank_AccountType.value =
                    accountTypeToAgeniumMap[banking.accountType.value as 'CHECKING' | 'SAVINGS'];
                case_Applicant_LapseNotice.value = 'No';
                case_Applicant_PaymentMode.value = 'Automatic EFT (monthly draft)';
                case_Applicant_OtherPaymentMode.value = null;
                case_Applicant_PaymentModeEFTInitialDraft.value = 'Draft initial premium when policy is issued';
                case_Applicant_PaymentFrequency.value = 'Monthly';
                case_Applicant_PaymentFrequencyOther.value = 'Monthly';
                case_Applicant_RequestedEffectiveDate.value = format(selectedEffectiveDate, 'MM/dd/yyyy');
                case_AgentReport_AgentAssisted.value = 'Yes';
                // since we have mix of applications from v7 and v8 which may or may not have this field, we need to
                // take care of both cases.
                case_AgentReport_AgentAssistedContractNumber.value = agentDetail?.Case_Agent_KeyedContractNumber;
                // TODO: Temporary fix for displaying effective date on All Done page
                parameterStore.getQuote()?.prosperEffectiveDate.setValue(selectedEffectiveDate);
                if (ageniumContext.sdk) {
                    try {
                        const questionsToUpdate = [
                            case_Applicant_PaymentMode,
                            case_Applicant_PaymentModeEFTInitialDraft,
                            case_Bank_AccountType,
                            case_Bank_RoutingNumber,
                            case_Bank_AccountNumber,
                            case_Applicant_PaymentFrequency,
                            case_Applicant_LapseNotice,
                            case_Applicant_RequestedEffectiveDate,
                            case_AgentReport_AgentAssisted,
                            case_AgentReport_AgentAssistedContractNumber,
                        ];
                        for (const q of questionsToUpdate) {
                            if (q) {
                                await ageniumContext.submitAnswer(q);
                            }
                        }
                        await callNext();
                    } catch (e) {
                        console.log(e);
                        onError(e);
                    }
                } else alert('SDK is not READY');
            }
        };

        async function callNext() {
            if (isFirstNextCall && ageniumContext.sdk && case_Applicant_FaceAmountFinal) {
                setIsFirstNextCall(false);
                if (calcFocusDataLocal && calcFocusDataLocal.out && calcFocusDataLocal.out.length)
                    case_Applicant_FaceAmountFinal.value = calcFocusDataLocal.out.find(
                        (t: Outlist) => t.fieldName === 'faceAmount',
                    )?.value;
                else
                    case_Applicant_FaceAmountFinal.value = (
                        await getApprovalQuoteFromCalcfocus(
                            ageniumContext.applicationData as ApplicationData[],
                            ageniumUser,
                        )
                    )?.out?.find((t: Outlist) => t.fieldName === 'faceAmount')?.value;
                try {
                    if (await firstValueFrom(ageniumContext.sdk.submitAnswer(case_Applicant_FaceAmountFinal))) onNext();
                } catch (e) {
                    onError('Error during submission of the Applicant Face Amount Final');
                }
            }
        }

        const minDateCalc = () => {
            const date = new Date();
            if (isEstLateHour()) date.setDate(date.getDate() + 1);
            return date;
        };

        const maxDateCalc = (case_UWDecision_OverallDateVMK = '', birthdayMMDD: number[] = []) => {
            const date = minDateCalc();
            const fromDate = case_UWDecision_OverallDateVMK ? new Date(case_UWDecision_OverallDateVMK) : date;
            const maxEffectiveDateRange =
                fromDate.getTime() >= NEW_MAX_EFFECTIVE_ENFORCE_DT.getTime() ? (effectiveDateOverride ? 43 : 30) : 40;
            if (birthdayMMDD.length === 0)
                return new Date(fromDate.setDate(fromDate.getDate() + maxEffectiveDateRange));
            const birthday = new Date(
                `${birthdayMMDD[0].toString().padStart(2, '0')}/${birthdayMMDD[1].toString().padStart(2, '0')}/${fromDate.getFullYear()}`,
            );
            const maxDate = new Date(fromDate.setDate(fromDate.getDate() + maxEffectiveDateRange));
            return fromDate < birthday && birthday < maxDate ? birthday : maxDate;
        };

        const compareDatesOnly = (date1: Date, date2: Date): number => {
            const yearDiff = date1.getFullYear() - date2.getFullYear();
            if (yearDiff !== 0) return yearDiff;
            const monthDiff = date1.getMonth() - date2.getMonth();
            if (monthDiff !== 0) return monthDiff;
            return date1.getDate() - date2.getDate();
        };

        const [minDate] = useState<Date>(minDateCalc());
        const [maxDate, setMaxDate] = useState<Date>();

        useEffect(() => {
            if (
                ageniumContext.applicationData &&
                ageniumContext.applicationData.find((t) => t.key === 'Case_Applicant_DOB')?.value &&
                ageniumContext.applicationData.find((t) => t.key === 'Case_UWDecision_OverallDateVMK')?.value
            ) {
                const dob = ageniumContext.applicationData.find((t) => t.key === 'Case_Applicant_DOB')?.value;
                let dobMMDD: number[] = [];
                if (dob) {
                    const date = new Date(dob);
                    dobMMDD = [date.getMonth() + 1, date.getDate()];
                }
                setMaxDate(
                    maxDateCalc(
                        ageniumContext.applicationData.find((t) => t.key === 'Case_UWDecision_OverallDateVMK')?.value,
                        dobMMDD,
                    ),
                );
            }
        }, [ageniumContext.applicationData]);

        const maskRoutingNumber = new MaskedPattern({mask: '{000000000}'});
        const maskAccountNumber = new MaskedPattern({mask: '{00000000000000000}'});

        return (
            <Grid container classes={{root: classes.wrapper}}>
                <Typography classes={{root: classes.title}} data-testId="bankingInfo">
                    Let's Get Some Banking Information.
                </Typography>
                <FormSection title="Basics">
                    <Grid container spacing={4} justify="center">
                        <Grid item xs={3}>
                            <Typography className={classes.label}>Bank Account Type</Typography>
                            <FormControl fullWidth variant="outlined" className={classes.formControl}>
                                <Select
                                    aria-label="Bank Account Type"
                                    variant="outlined"
                                    value={banking.accountType.value || 'CHECKING'}
                                    onChange={(e) => onValueChangedBanking(banking.accountType, e.target.value)}
                                    placeholder="--"
                                >
                                    {[
                                        ['CHECKING', 'Checking'],
                                        ['SAVINGS', 'Savings'],
                                    ].map(([value, label]) => (
                                        <MenuItem key={value} value={value}>
                                            {label}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.label}>Routing Number</Typography>
                            <InputText
                                aria-label="Routing Number"
                                mask={maskRoutingNumber}
                                value={banking.routingNumber.value}
                                onChange={(e) => onValueChangedBanking(banking.routingNumber, e.target.value)}
                                placeholder="--"
                            />
                        </Grid>
                        <Grid item xs={3}>
                            <Typography className={classes.label}>Account Number</Typography>
                            <InputText
                                aria-label="Account Number"
                                mask={maskAccountNumber}
                                value={banking.accountNumber.value}
                                onChange={(e) => onValueChangedBanking(banking.accountNumber, e.target.value)}
                                placeholder="--"
                            />
                        </Grid>
                        <Grid item xs={3} />
                        <Grid item xs={3} className={classes.datePicker}>
                            <Typography className={classes.label}>
                                Effective Date{' '}
                                {!ageniumContext.applicationData ||
                                    (!maxDate && <CircularProgress size={10} color="inherit" />)}
                            </Typography>
                            <MuiPickersUtilsProvider utils={DateFnsUtils}>
                                <KeyboardDatePicker
                                    inputVariant="outlined"
                                    fullWidth
                                    disablePast
                                    shouldDisableDate={effectiveDatesDisable}
                                    margin="normal"
                                    variant="inline"
                                    aria-label="Effective Date"
                                    format="MM/dd/yyyy"
                                    minDate={minDate}
                                    maxDate={maxDate || minDate}
                                    value={selectedEffectiveDate}
                                    onChange={setSelectedEffectiveDate}
                                    KeyboardButtonProps={{
                                        'aria-label': 'change effective date',
                                    }}
                                    disabled={!ageniumContext.applicationData || !maxDate}
                                    disableToolbar
                                />
                            </MuiPickersUtilsProvider>
                        </Grid>
                        <Grid item xs={9} />
                        {bankVerificationResult && bankVerificationResult.state !== BankVerificationState.PASS && (
                            <Grid item xs={12}>
                                <Box className={classes.errorMessage}>
                                    {bankVerificationResult.failureMessage ||
                                        'We could not verify the bank account details. Please confirm with the client or correct the information.'}
                                </Box>
                                {bankVerificationResult.state === BankVerificationState.PREVENT_NEXT_WITH_OVERRIDE && (
                                    <FormControlLabel
                                        className={classes.overrideCheckbox}
                                        control={
                                            <Checkbox
                                                checked={overrideConfirmed}
                                                onChange={(e) => {
                                                    const isChecked = e.target.checked;
                                                    setOverrideConfirmed(isChecked);
                                                }}
                                                color="primary"
                                            />
                                        }
                                        label="I acknowledge the bank info failed validation but have confirmed accuracy with the client."
                                    />
                                )}
                            </Grid>
                        )}
                        <Grid item xs={12}>
                            <Box className={classes.disclamerWrapper}>
                                <Typography className={classes.disclamerTitle}>Effective Date Explanation</Typography>
                                <Typography className={classes.disclamerText}>
                                    Please ensure the client understands that their Effective Date would be the date
                                    their Coverage would start assuming they sign the policy on or before that date. If
                                    they sign the policy after their Requested Effective Date, then their new Effective
                                    Date will be the date they sign. They will be drafted on the date that their policy
                                    is Effective, and the "Day" of their Effective Date will be when they are drafted
                                    each month.
                                </Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </FormSection>
                <Grid container spacing={2} justify="space-around" classes={{root: classes.navigation}}>
                    <Grid item xs={2}>
                        <Button
                            color="primary"
                            variant="text"
                            onClick={onExit}
                            startIcon={<KeyboardBackspaceIcon />}
                            classes={{root: classes.backBtn}}
                        >
                            Save and exit
                        </Button>
                    </Grid>
                    {onBack && (
                        <Grid item className={classes.btnWrapper}>
                            <Button color="primary" variant="outlined" onClick={onBack} className={classes.nextBtn}>
                                BACK
                            </Button>
                        </Grid>
                    )}
                    <Grid item className={classes.btnWrapper}>
                        <Button
                            color="primary"
                            variant="contained"
                            onClick={submitValues}
                            classes={{root: classes.nextBtn}}
                            disabled={
                                !ageniumContext.applicationData ||
                                submitting ||
                                verificationInProgress ||
                                !(
                                    case_Bank_RoutingNumber &&
                                    case_Bank_AccountNumber &&
                                    case_Bank_AccountType &&
                                    case_Applicant_LapseNotice &&
                                    case_Applicant_PaymentMode &&
                                    case_Applicant_OtherPaymentMode &&
                                    case_Applicant_PaymentModeEFTInitialDraft &&
                                    case_Applicant_PaymentFrequency &&
                                    case_Applicant_PaymentFrequencyOther &&
                                    case_Applicant_RequestedEffectiveDate &&
                                    banking.routingNumber.value &&
                                    banking.accountNumber.value &&
                                    banking.accountType.value &&
                                    selectedEffectiveDate &&
                                    maxDate
                                ) ||
                                !!(maxDate && compareDatesOnly(maxDate, minDate) < 0) ||
                                !!(bankVerificationResult && !canProceedWithVerification())
                            }
                        >
                            {!ageniumContext.applicationData || submitting || verificationInProgress ? (
                                <>
                                    <CircularProgress size={16} color="inherit" />
                                    <span style={{marginLeft: '10px'}}>
                                        {verificationInProgress
                                            ? 'Verifying Bank Account'
                                            : ageniumContext.applicationData
                                              ? 'Next'
                                              : 'Loading Application'}
                                    </span>
                                </>
                            ) : (
                                <>Next</>
                            )}
                        </Button>
                    </Grid>
                </Grid>
            </Grid>
        );
    },
);

export default BankingInformation;
